// approver-onboarding-modal/context.tsx
import React, { createContext, useContext, useState, ReactNode, useMemo } from 'react'
import { IUserInfo } from '../../types/users'
import { saveApproverOnboardingProgress, ApproverOnboardingProgress } from '../../util/approver-onboarding-storage'
import { track } from '../../services/analytics/analytics'

interface ApproverOnboardingContextState {
  currentStep: number
  departmentName: string // Primary department (first one) - kept for backward compatibility
  departmentNames: string[] // All departments the approver is assigned to
  authUser?: IUserInfo
  companyId?: string
  isLoading: boolean
}

interface ApproverOnboardingContextActions {
  nextStep: () => void
  previousStep: () => void
  goToStep: (step: number) => void
  setLoading: (loading: boolean) => void
  markComplete: () => void
}

type ApproverOnboardingContextType = ApproverOnboardingContextState & ApproverOnboardingContextActions

const ApproverOnboardingContext = createContext<ApproverOnboardingContextType | undefined>(undefined)

interface ApproverOnboardingProviderProps {
  children: ReactNode
  authUser: IUserInfo
  companyId: string
  departmentNames: string[] | string // Accept both for safety
  initialProgress?: ApproverOnboardingProgress
}

export const ApproverOnboardingProvider: React.FC<ApproverOnboardingProviderProps> = ({ children, authUser, companyId, departmentNames: departmentNamesProp, initialProgress }) => {
  const [currentStep, setCurrentStep] = useState(initialProgress?.currentStep || 0)
  const [isLoading, setIsLoading] = useState(false)

  // Normalize departmentNames to always be an array
  // This handles the case where a string is accidentally passed instead of an array
  const normalizedDepartmentNames = useMemo(() => {
    if (!departmentNamesProp) {
      return []
    }
    // If it's already an array, filter to valid strings
    if (Array.isArray(departmentNamesProp)) {
      return departmentNamesProp.filter((name): name is string => Boolean(name && typeof name === 'string'))
    }
    // If it's a single string, wrap it in an array
    if (typeof departmentNamesProp === 'string' && departmentNamesProp.length > 0) {
      return [departmentNamesProp]
    }
    return []
  }, [departmentNamesProp])

  // Primary department name (first one) for backward compatibility
  const departmentName = normalizedDepartmentNames[0] || ''

  const nextStep = () => {
    const newStep = Math.min(currentStep + 1, 3) // Max 4 steps (0, 1, 2, 3)
    setCurrentStep(newStep)
    saveApproverOnboardingProgress(authUser.id, companyId, newStep, departmentName)
    track('APPROVER_ONBOARDING_STEP_PROGRESSED', {
      company_id: companyId,
      user_id: authUser.id,
      from_step: currentStep,
      to_step: newStep,
    })
  }

  const previousStep = () => {
    const newStep = Math.max(currentStep - 1, 0) // Min step is 0
    setCurrentStep(newStep)
    saveApproverOnboardingProgress(authUser.id, companyId, newStep, departmentName)
  }

  const goToStep = (step: number) => {
    const clampedStep = Math.max(0, Math.min(step, 3))
    setCurrentStep(clampedStep)
    saveApproverOnboardingProgress(authUser.id, companyId, clampedStep, departmentName)
  }

  const setLoading = (loading: boolean) => {
    setIsLoading(loading)
  }

  const markComplete = () => {
    // Track onboarding completion
    track('APPROVER_ONBOARDING_COMPLETED', {
      company_id: companyId,
      user_id: authUser.id,
      email: authUser.email,
      department: departmentName,
      departments: normalizedDepartmentNames,
    })

    saveApproverOnboardingProgress(authUser.id, companyId, 3, departmentName, true)
  }

  const contextValue: ApproverOnboardingContextType = {
    currentStep,
    departmentName,
    departmentNames: normalizedDepartmentNames,
    authUser,
    companyId,
    isLoading,
    nextStep,
    previousStep,
    goToStep,
    setLoading,
    markComplete,
  }

  return <ApproverOnboardingContext.Provider value={contextValue}>{children}</ApproverOnboardingContext.Provider>
}

export const useApproverOnboardingContext = () => {
  const context = useContext(ApproverOnboardingContext)
  if (context === undefined) {
    throw new Error('useApproverOnboardingContext must be used within an ApproverOnboardingProvider')
  }
  return context
}
